import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { useLogoTheme } from '../context/LogoThemeContext';
import { fetchItemBySlug } from '../lib/cms';

export default function LytFraHjertetPage() {
  const { headerBgColor } = useLogoTheme();
  const [pageData, setPageData] = useState(null);

  useEffect(() => {
    async function loadData() {
      const data = await fetchItemBySlug('forlob_sider', 'lyt-fra-hjertet');
      if (data) setPageData(data);
    }
    loadData();
  }, []);

  const heroBadge = pageData?.hero_badge || 'Månedligt fællesskab for kvinder';
  const heroOverskrift = pageData?.hero_overskrift || 'LYT – FRA HJERTET';
  const heroUndertitel = pageData?.hero_undertitel || 'Et trygt rum, hvor du må dele og bare blive hørt';
  const badge1 = pageData?.badge_linje_1 || 'Én gang om måneden · Ca. 2 timer';
  const badge2 = pageData?.badge_linje_2 || 'Lille gruppe · 200 kr. pr. gang';
  const prisTekst = pageData?.pris || '200 kr. pr. gang';
  const rammerOverskrift = pageData?.rammer_overskrift || 'Praktisk';
  const rammerPunkter = pageData?.rammer_punkter;
  const ctaTekst = pageData?.cta_tekst || 'Tilmeld dig næste samling / Kontakt';

  return (
    <>
      <SEO
        title="Lyt – fra Hjertet - Lenea Evolinee"
        description="Et trygt rum, hvor du må dele og bare blive hørt. Månedligt fællesskab for kvinder hos Lenea Evolinee."
      />

      {/* Hero Header */}
      <section style={{ backgroundColor: headerBgColor }} className="pt-36 pb-20 transition-colors duration-300">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-4">
          <span className="text-xs uppercase tracking-widest text-[#98866C] font-semibold block">
            {heroBadge}
          </span>
          <h1 className="font-serif text-4xl sm:text-5xl font-normal text-[#2E2A27]">
            {heroOverskrift}
          </h1>
          <p className="text-lg sm:text-xl text-[#5C524A] leading-relaxed max-w-2xl mx-auto font-sans font-medium">
            {heroUndertitel}
          </p>
          <div className="pt-4 flex flex-wrap justify-center gap-3">
            <span className="inline-block px-4 py-1.5 rounded-full bg-[#FAF7F2] border border-[#D8CDBF] text-xs font-semibold text-[#2E2A27] tracking-wider uppercase">
              {badge1}
            </span>
            <span className="inline-block px-4 py-1.5 rounded-full bg-[#F3E6E2] border border-[#D8ADA0]/40 text-xs font-semibold text-[#864B3D] tracking-wider uppercase">
              {badge2}
            </span>
          </div>
        </div>
      </section>

      {/* Sektion 1: Indhold fra CMS */}
      <section className="py-20 bg-white border-y border-[#E8DFD5]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {pageData?.indhold_html ? (
            <div
              className="space-y-6 text-base sm:text-lg text-[#5C524A] leading-relaxed font-sans cms-richtext"
              dangerouslySetInnerHTML={{ __html: pageData.indhold_html }}
            />
          ) : (
            <div className="space-y-6 text-base sm:text-lg text-[#5C524A] leading-relaxed font-sans">
              <p className="font-serif text-2xl sm:text-3xl text-[#2E2A27] font-normal">
                Har du nogensinde haft brug for bare at sige det højt?
              </p>
              <div className="space-y-1.5 text-[#2E2A27] font-medium py-2">
                <p>Ikke fordi nogen skal finde en løsning.</p>
                <p>Ikke fordi du har brug for gode råd.</p>
                <p className="text-[#98866C]">Men fordi det kan gøre en forskel at blive lyttet til.</p>
              </div>
              <p>
                At få lov til at sætte ord på det, der fylder, uden at blive afbrudt, analyseret eller fortalt, hvad du burde gøre.
              </p>
              <p>
                Lyt – fra Hjertet er et månedligt rum for kvinder, hvor vi mødes i nærvær og fortrolighed.
              </p>
              <p className="font-medium text-[#2E2A27]">
                Et rum, hvor du må komme præcis, som du er.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Sektion 2: Du behøver ikke have styr på det */}
      <section className="py-20 bg-[#FAF7F2]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
          <div className="space-y-4">
            <h2 className="font-serif text-3xl sm:text-4xl font-normal text-[#2E2A27]">
              {pageData?.sektion_2_overskrift || 'Du behøver ikke have styr på det'}
            </h2>
            <div className="w-16 h-0.5 bg-[#98866C]/60" />
          </div>

          {pageData?.sektion_2_tekst ? (
            <div
              className="space-y-6 text-base sm:text-lg text-[#5C524A] leading-relaxed font-sans cms-richtext"
              dangerouslySetInnerHTML={{ __html: pageData.sektion_2_tekst }}
            />
          ) : (
            <div className="space-y-6 text-base sm:text-lg text-[#5C524A] leading-relaxed font-sans">
              <p>
                Måske går du med noget, du har brug for at få sagt højt.
              </p>
              <p>
                Måske står du midt i en forandring, en tvivl eller noget, der fylder i dit liv.
              </p>
              <p>
                Måske er der noget, du endnu ikke helt kan sætte ord på.
              </p>
              <p>
                Eller måske har du bare brug for at mærke, at du ikke står alene.
              </p>

              <div className="p-6 sm:p-8 bg-white rounded-sm border border-[#D8CDBF] space-y-2 text-[#2E2A27] font-medium">
                <p>Her behøver du ikke have en færdig historie.</p>
                <p>Du behøver ikke vide, hvad du føler.</p>
                <p>Du behøver ikke finde løsningen.</p>
                <p className="text-xl font-serif text-[#98866C] pt-2">Du må bare dele det, der er.</p>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Sektion 3: Vi lytter – fra hjertet */}
      <section className="py-20 bg-white border-y border-[#E8DFD5]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
          <div className="space-y-4">
            <h2 className="font-serif text-3xl sm:text-4xl font-normal text-[#2E2A27]">
              {pageData?.sektion_3_overskrift || 'Vi lytter – fra hjertet'}
            </h2>
            <div className="w-16 h-0.5 bg-[#98866C]/60" />
          </div>

          {pageData?.sektion_3_tekst ? (
            <div
              className="space-y-6 text-base sm:text-lg text-[#5C524A] leading-relaxed font-sans cms-richtext"
              dangerouslySetInnerHTML={{ __html: pageData.sektion_3_tekst }}
            />
          ) : (
            <div className="space-y-6 text-base sm:text-lg text-[#5C524A] leading-relaxed font-sans">
              <p>
                Når én kvinde deler, lytter de andre.
              </p>

              <div className="space-y-1.5 text-[#2E2A27] font-medium py-2">
                <p>Vi kommer ikke med råd.</p>
                <p>Vi analyserer ikke.</p>
                <p>Vi fortæller ikke, hvad hun skal gøre.</p>
              </div>

              <p className="font-serif text-2xl text-[#2E2A27]">
                Vi lytter.
              </p>
              <p>
                Med nærvær, respekt og åbenhed.
              </p>
              <p>
                For nogle gange er det mest kærlige, vi kan give et andet menneske, ikke et svar – men vores fulde tilstedeværelse.
              </p>
              <p>
                Og måske er det netop dét, du selv længes efter:
              </p>
              <p className="font-medium text-[#2E2A27]">
                At blive hørt uden at skulle forklare dig.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Sektion 4: Du må dele – eller bare lytte */}
      <section className="py-20 bg-[#FAF7F2]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
          <div className="space-y-4">
            <h2 className="font-serif text-3xl sm:text-4xl font-normal text-[#2E2A27]">
              {pageData?.sektion_4_overskrift || 'Du må dele – eller bare lytte'}
            </h2>
            <div className="w-16 h-0.5 bg-[#98866C]/60" />
          </div>

          {pageData?.sektion_4_tekst ? (
            <div
              className="space-y-6 text-base sm:text-lg text-[#5C524A] leading-relaxed font-sans cms-richtext"
              dangerouslySetInnerHTML={{ __html: pageData.sektion_4_tekst }}
            />
          ) : (
            <div className="space-y-6 text-base sm:text-lg text-[#5C524A] leading-relaxed font-sans">
              <p>
                Du behøver ikke dele hver gang.
              </p>
              <p>
                Nogle gange har du måske noget på hjerte.
              </p>
              <p>
                Andre gange har du måske mest brug for at være stille og lytte.
              </p>
              <p className="font-medium text-[#2E2A27]">
                Begge dele er helt okay.
              </p>
              <p>
                Der er ingen præstation og ingen forventning.
              </p>
              <p className="font-serif text-xl text-[#98866C]">
                Du mærker selv efter.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Sektion 5: Et lille månedligt pusterum */}
      <section className="py-20 bg-white border-y border-[#E8DFD5]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
          <div className="space-y-4">
            <h2 className="font-serif text-3xl sm:text-4xl font-normal text-[#2E2A27]">
              {pageData?.sektion_5_overskrift || 'Et lille månedligt pusterum'}
            </h2>
            <div className="w-16 h-0.5 bg-[#98866C]/60" />
          </div>

          {pageData?.sektion_5_tekst ? (
            <div
              className="space-y-6 text-base sm:text-lg text-[#5C524A] leading-relaxed font-sans cms-richtext"
              dangerouslySetInnerHTML={{ __html: pageData.sektion_5_tekst }}
            />
          ) : (
            <div className="space-y-6 text-base sm:text-lg text-[#5C524A] leading-relaxed font-sans">
              <p>
                Vi mødes én gang om måneden i min klinik.
              </p>
              <p>
                Vi begynder med at lande sammen – måske gennem et par rolige vejrtrækninger, en kort meditation eller en enkel øvelse, der hjælper os med at komme til stede.
              </p>
              <p>
                Derefter åbner vi rummet.
              </p>
              <p>
                Der er ingen fast dagsorden.
              </p>
              <p>
                Vi tager udgangspunkt i det, der er levende lige nu.
              </p>
              <p>
                Det kan være stort eller småt.
              </p>
              <p>
                Glæde, sorg, frustration, tvivl, længsel, kærlighed, relationer, hverdagen – eller noget helt andet.
              </p>
              <p className="font-medium text-[#2E2A27]">
                Der er plads til det hele.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Sektion 6: Et rum med fortrolighed */}
      <section className="py-20 bg-[#FAF7F2]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
          <div className="space-y-4">
            <h2 className="font-serif text-3xl sm:text-4xl font-normal text-[#2E2A27]">
              {pageData?.sektion_6_overskrift || 'Et rum med fortrolighed'}
            </h2>
            <div className="w-16 h-0.5 bg-[#98866C]/60" />
          </div>

          {pageData?.sektion_6_tekst ? (
            <div
              className="space-y-6 text-base sm:text-lg text-[#5C524A] leading-relaxed font-sans cms-richtext"
              dangerouslySetInnerHTML={{ __html: pageData.sektion_6_tekst }}
            />
          ) : (
            <div className="space-y-6 text-base sm:text-lg text-[#5C524A] leading-relaxed font-sans">
              <p>
                For mig er tryghed og fortrolighed fundamentet.
              </p>
              <p>
                Det, der bliver delt i gruppen, bliver i gruppen.
              </p>
              <p>
                Vi passer på hinandens historier.
              </p>
              <p>
                Vi respekterer hinandens grænser.
              </p>
              <p>
                Og vi øver os i at være sammen uden at skulle ændre eller fikse hinanden.
              </p>

              <div className="p-6 sm:p-8 bg-white rounded-sm border border-[#D8CDBF] space-y-4 mt-6">
                <h3 className="font-serif text-2xl text-[#2E2A27]">
                  Lyt – fra Hjertet
                </h3>
                <div className="space-y-1.5 text-base sm:text-lg text-[#5C524A]">
                  <p>Et sted, hvor du ikke behøver have svarene.</p>
                  <p>Et sted, hvor du ikke skal være stærk.</p>
                  <p>Et sted, hvor du må sætte ord på det, der fylder.</p>
                  <p>Et sted, hvor nogen bare lytter.</p>
                </div>
                <p className="font-serif text-xl text-[#98866C] pt-2">
                  Fra hjerte til hjerte.
                </p>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Sektion 7: Praktisk */}
      <section className="py-20 bg-white border-y border-[#E8DFD5]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[#FAF7F2] p-8 sm:p-12 rounded-sm border border-[#D8CDBF] shadow-xs space-y-8">
            <h2 className="font-serif text-3xl sm:text-4xl font-normal text-[#2E2A27]">
              {rammerOverskrift}
            </h2>

            <div className="space-y-5 text-base sm:text-lg text-[#5C524A] leading-relaxed font-sans">
              <div>
                <p className="font-serif text-xl sm:text-2xl text-[#2E2A27] font-medium">
                  Lyt – fra Hjertet
                </p>
                <p className="text-base text-[#98866C] font-medium">
                  Månedligt fællesskab for kvinder
                </p>
              </div>

              {rammerPunkter ? (
                <div className="space-y-2.5 text-[#2E2A27] font-medium">
                  {rammerPunkter.split('•').map((punkt, pIdx) => {
                    const clean = punkt.trim();
                    if (!clean) return null;
                    return (
                      <p key={pIdx} className="flex items-start gap-2">
                        <span>🌿</span>
                        <span>{clean}</span>
                      </p>
                    );
                  })}
                </div>
              ) : (
                <div className="space-y-2 text-[#2E2A27] font-medium">
                  <p>🌿 Én gang om måneden</p>
                  <p>🌿 Ca. 2 timer</p>
                  <p>🌿 Lille gruppe</p>
                  <p>🌿 Trygt og fortroligt rum</p>
                </div>
              )}

              <div className="p-6 bg-white rounded-sm border border-[#E8DFD5] space-y-2 mt-4">
                <p className="text-xl font-serif font-medium text-[#2E2A27]">
                  Pris: {prisTekst}
                </p>
                <p className="text-sm text-[#5C524A]">
                  Du tilmelder dig fra gang til gang, så du kan komme, når du mærker, at du har brug for rummet.
                </p>
              </div>

              <p className="pt-2 font-medium text-[#2E2A27]">
                Hvis du mærker, at dette kunne være noget for dig, er du hjerteligt velkommen.
              </p>
              <div className="space-y-1 text-[#5C524A]">
                <p>Du behøver ikke have noget særligt at komme med.</p>
                <p className="font-medium text-[#2E2A27]">Du må bare komme med dig.</p>
              </div>

              <div className="pt-4 font-serif text-xl text-[#2E2A27]">
                <p>Med kærlighed og åbent hjerte</p>
                <p className="italic text-[#98866C] pt-1">Lenea</p>
              </div>

              <div className="pt-4">
                <Link
                  to="/kontakt"
                  className="inline-flex items-center gap-2 px-8 py-4 bg-[#98866C] text-white font-medium text-xs uppercase tracking-widest hover:bg-[#83735C] transition-colors cursor-pointer"
                >
                  <span>{ctaTekst}</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
